import { useState } from 'react';
import { describeRule, ruleForKind, slugifyTag, type RepeatKind, type Tag } from '@hestia/core';
import { Icon, icons, hueVar, tintVar, onTintVar } from '../../ui.js';
import { weekday } from '../../lang.js';
import type { Draft } from './draft.js';

/** The panels that open under the chip strip. Each is one level down and no
 *  more: nothing here opens anything else. */

/** Six times covering the shape of a household day. Anything else goes through
 *  the native control, which on a phone is the system wheel — a wheel is a poor
 *  way to pick a date out of a decade, and a perfectly good way to pick a minute
 *  out of an hour. */
const TIMES = ['07:00', '09:00', '12:00', '15:00', '18:00', '20:00'];
const DURATIONS: Array<[string, number]> = [['15m', 15], ['30m', 30], ['1h', 60], ['2h', 120]];

export function TimePanel({ d, set }: { d: Draft; set: (p: Partial<Draft>) => void }) {
  return (
    <div className="panel-body">
      <div className="chiprow">
        {TIMES.map((t) => (
          <button key={t} type="button" className={`qchip${d.time === t ? ' on' : ''}`}
                  aria-pressed={d.time === t} onClick={() => set({ time: t })}>{t}</button>
        ))}
        <label className={`qchip qchip-field${d.time && !TIMES.includes(d.time) ? ' on' : ''}`}>
          <Icon path={icons.clock} size={15} />
          <input type="time" step={900} value={d.time ?? ''} aria-label="Another time"
                 onChange={(e) => set({ time: e.target.value || null })} />
        </label>
      </div>

      {/* Duration is the rarest thing on a task and means nothing without a
          time, so it does not exist until there is one. */}
      {d.time && (
        <>
          <div className="sec">How long</div>
          <div className="chiprow">
            <button type="button" className={`qchip${d.durationMin === null ? ' on' : ''}`}
                    onClick={() => set({ durationMin: null })}>Any</button>
            {DURATIONS.map(([label, mins]) => (
              <button key={mins} type="button" className={`qchip${d.durationMin === mins ? ' on' : ''}`}
                      aria-pressed={d.durationMin === mins}
                      onClick={() => set({ durationMin: mins })}>{label}</button>
            ))}
          </div>
        </>
      )}

      {d.time && (
        <button type="button" className="panel-clear" onClick={() => set({ time: null, durationMin: null })}>
          Any time of day
        </button>
      )}
    </div>
  );
}

const DOW = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];

export function RepeatPanel({ d, original, set }: {
  d: Draft; original: string | null; set: (p: Partial<Draft>) => void;
}) {
  // Named after the day the task actually falls on, so the choice reads as a
  // sentence about this task rather than as a setting.
  const options: Array<[RepeatKind, string]> = [
    ['none', 'Just once'],
    ['daily', 'Every day'],
    ['weekdays', 'Weekdays'],
    ['weekly', `Every ${weekday(d.date)}`],
    ['monthly', 'Every month'],
  ];
  return (
    <div className="panel-body">
      <div className="chiprow">
        {options.map(([kind, label]) => (
          <button key={kind} type="button" className={`qchip${d.repeat === kind ? ' on' : ''}`}
                  aria-pressed={d.repeat === kind}
                  onClick={() => set({ repeat: kind, weekdays: kind === 'weekly' ? d.weekdays : [] })}>
            {label}
          </button>
        ))}
        {/* A rule that arrived from quick-add or the bot and that none of these
            buttons can describe. Offered as it is, never rounded to the nearest. */}
        {d.repeat === 'custom' && (
          <button type="button" className="qchip on" aria-pressed>
            {describeRule(original, { short: true }) || 'Repeats'}
          </button>
        )}
      </div>

      {d.repeat === 'weekly' && (
        <div className="dow">
          {DOW.map((label, i) => {
            const on = d.weekdays.includes(i);
            return (
              <button key={label} type="button" aria-pressed={on} aria-label={weekday(d.date)}
                      className={`dow-day${on ? ' on' : ''}`}
                      onClick={() => set({
                        weekdays: on ? d.weekdays.filter((n) => n !== i)
                                     : [...d.weekdays, i].sort((a, b) => a - b),
                      })}>
                {label}
              </button>
            );
          })}
        </div>
      )}

      {d.repeat !== 'none' && (
        <div className="dp-echo">
          {describeRule(d.repeat === 'custom' ? original : ruleForKind(d.repeat, d.weekdays))}
          {d.weekdays.length === 0 && d.repeat === 'weekly' ? ` (on a ${weekday(d.date)})` : ''}
        </div>
      )}
    </div>
  );
}

export function TagPanel({ d, tags, set }: {
  d: Draft; tags: Tag[]; set: (p: Partial<Draft>) => void;
}) {
  const [typed, setTyped] = useState('');
  const known = new Map(tags.map((t) => [t.slug, t]));
  const toggle = (slug: string) =>
    set({ tags: d.tags.includes(slug) ? d.tags.filter((s) => s !== slug) : [...d.tags, slug] });

  function add() {
    const slug = slugifyTag(typed);
    if (slug && !d.tags.includes(slug)) set({ tags: [...d.tags, slug] });
    setTyped('');
  }

  return (
    <div className="panel-body">
      <div className="chiprow">
        {tags.map((t) => {
          const on = d.tags.includes(t.slug);
          return (
            <button key={t.id} type="button" aria-pressed={on} onClick={() => toggle(t.slug)}
                    className="tagbtn"
                    style={{ background: on ? hueVar(t.hue) : tintVar(t.hue),
                             color: on ? '#fff' : onTintVar(t.hue) }}>
              {t.name}
            </button>
          );
        })}
        {/* Tags typed here but not yet on the server — they exist the moment the
            task is saved, and look the part in the meantime. */}
        {d.tags.filter((s) => !known.has(s)).map((s) => (
          <button key={s} type="button" className="tagbtn"
                  style={{ background: 'var(--accent)', color: '#fff' }}
                  onClick={() => toggle(s)}>
            {s}<Icon path={icons.x} size={11} w={2.4} />
          </button>
        ))}
        <input className="tag-input" value={typed} placeholder="+ new" aria-label="Add a tag"
               onChange={(e) => setTyped(e.target.value)}
               onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
               onBlur={add} />
      </div>
    </div>
  );
}
